/**
 * World Globe Level Gallery (Jonathan 2026-05-21) — the market's second room.
 *
 * Rebuilt as a walkable single-column board (sibling of the Tausch trade
 * board): the same knight avatar jumps between player-created level cards to
 * select them, with the exit door on the left (back to Tausch) and the scroll
 * indicator on the right. The standing-selected level surfaces its peer
 * reviews ("Gutes Level", "Bester Creator") in a panel above the hero.
 *
 * Shares all the room/stack/scroll/door/tab machinery with ShopScene via
 * PlatformBoardScene; this subclass only supplies the level data, the MapCard
 * renderer, the door config, and the trades round-trip.
 */
import Phaser from "phaser";

import { GAMEPLAY, NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";
import type { StringKey } from "../i18n";

import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { GalleryService, type GalleryLevel } from "../services/GalleryService";
import { MapCard } from "../ui/MapCard";
import {
  FLOOR_Y,
  PlatformBoardScene,
  type BoardCard,
  type BoardSceneInit,
  type DoorsConfig,
} from "./PlatformBoardScene";
import type { BoardTab, SpawnEntry } from "./board/navigation";

/** Localized peer-review snippets; indices match GalleryService.reviewIndices. */
const REVIEW_KEYS: StringKey[] = [
  "review.snippet.0",
  "review.snippet.1",
  "review.snippet.2",
  "review.snippet.3",
  "review.snippet.4",
  "review.snippet.5",
  "review.snippet.6",
];

const DEPTH_HUD = 20;

export class GalleryScene extends PlatformBoardScene {
  protected activeTab: BoardTab = "gallery";

  private identity!: AuthorIdentityService;
  private telemetry!: {
    emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void;
  };
  private levels: GalleryLevel[] = [];
  private reviewPanel!: Phaser.GameObjects.Container;
  private reviewBg!: Phaser.GameObjects.Rectangle;
  private reviewText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: "GalleryScene", physics: { arcade: { gravity: { x: 0, y: GAMEPLAY.gravity } } } });
  }

  init(data: Partial<BoardSceneInit>): void {
    this.initBoard(data);
  }

  create(): void {
    this.identity =
      (this.registry.get("authorIdentityService") as AuthorIdentityService | undefined) ??
      new AuthorIdentityService();
    this.telemetry =
      (this.registry.get("telemetryService") as GalleryScene["telemetry"] | undefined) ??
      { emit: () => undefined };
    this.levels = GalleryService.levels(REVIEW_KEYS.length);

    this.buildBackground();
    // Build the review panel BEFORE buildBoard(): buildBoard() renders the
    // cards, which synchronously calls refreshSelectionHighlight() ->
    // onSelectionChanged(), and that reads this.reviewPanel.
    this.buildReviewPanel();
    this.buildBoard();
    this.buildRightWall();

    this.telemetry.emit(1, "ugc.gallery.open", { count: this.levels.length });
  }

  // ---- PlatformBoardScene hooks -------------------------------------------

  protected visibleRows(): number {
    return GAMEPLAY.shopBoardVisibleCards;
  }

  protected itemCount(): number {
    return this.levels.length;
  }

  protected createCard(itemIndex: number, x: number, y: number): BoardCard {
    return new MapCard(this, x, y, this.levels[itemIndex]!, this.identity);
  }

  protected configureDoors(): DoorsConfig {
    // Left door goes back to the Tausch room; the right edge is the scroll
    // indicator (no right door). Esc/Start leaves the market from here.
    return { left: { action: "toTrades", labelKey: "gallery.toTrades", render: "signpost" } };
  }

  protected onSelectConfirm(selectedBufferIndex: number): void {
    if (selectedBufferIndex < 0) return;
    const level = this.levels[selectedBufferIndex];
    if (level) this.telemetry.emit(1, "ugc.gallery.level.view", { id: level.id });
  }

  protected override onSelectionChanged(selectedBufferIndex: number): void {
    // The base calls this during buildBoard()'s first render; the panel may
    // not be built yet on that very first call.
    if (!this.reviewPanel) return;
    const level = selectedBufferIndex < 0 ? undefined : this.levels[selectedBufferIndex];
    if (!level) {
      this.reviewPanel.setVisible(false);
      return;
    }
    const reviews = level.reviewIndices.map((i) => `"${t(REVIEW_KEYS[i]!)}"`).join("   ·   ");
    this.reviewText.setText(reviews);
    this.reviewBg.setSize(this.reviewText.width + 10, this.reviewText.height + 6);
    this.reviewPanel.setVisible(true);
  }

  protected override updateExtras(): void {
    // Float the review panel above the hero while a level is selected.
    if (this.reviewPanel.visible) this.reviewPanel.setPosition(this.hero.x, this.hero.y - 46);
  }

  protected exitMarket(): void {
    this.telemetry.emit(1, "ugc.gallery.exit", { returnTo: this.returnTo });
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else {
      this.scene.start("launcher");
    }
  }

  protected switchTab(target: BoardTab, entry: SpawnEntry): void {
    if (target !== "trades") return;
    this.telemetry.emit(1, "ugc.gallery.tab.trades", { via: this.switchVia });
    this.scene.start("ShopScene", { returnTo: this.returnTo, walletSource: this.walletSource, entry });
  }

  // ---- local fixtures -----------------------------------------------------

  /**
   * Invisible wall on the right. The Weltkugel has no right door (the right
   * edge is the scroll indicator), so the hero is walled off just left of it
   * rather than being allowed to wander onto the indicator / screen edge.
   */
  private buildRightWall(): void {
    const wall = this.add
      .rectangle(NATIVE_WIDTH - 28, NATIVE_HEIGHT / 2, 8, NATIVE_HEIGHT + 80, 0x000000, 0)
      .setVisible(false);
    this.physics.add.existing(wall, true);
    this.physics.add.collider(this.hero, wall);
  }

  private buildBackground(): void {
    this.cameras.main.setBackgroundColor(PALETTE.shopWall);
    this.add.rectangle(0, 0, NATIVE_WIDTH, 24, PALETTE.shopWallHi, 1).setOrigin(0, 0);

    // Floor band so the hero reads as standing on ground (mirrors the shop).
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, NATIVE_HEIGHT - FLOOR_Y, PALETTE.shopFloor, 1).setOrigin(0, 0);
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, 1, PALETTE.shopFloorEdge, 1).setOrigin(0, 0);
    for (let x = 16; x < NATIVE_WIDTH; x += 24) {
      this.add.rectangle(x, FLOOR_Y + 2, 1, NATIVE_HEIGHT - FLOOR_Y - 4, PALETTE.shopFloorEdge, 0.6).setOrigin(0, 0);
    }

    this.add.rectangle(4, 4, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    // Bottom control hint — the tab strip + back-to-Tausch door are drawn by
    // PlatformBoardScene.
    this.add
      .text(NATIVE_WIDTH / 2, NATIVE_HEIGHT - 8, t("gallery.hint"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.hex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH_HUD);
  }

  private buildReviewPanel(): void {
    this.reviewBg = this.add
      .rectangle(0, 0, 80, 12, PALETTE.hudPanel, 0.92)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, UGC_CYAN);
    this.reviewText = this.add
      .text(0, 0, "", { fontFamily: "monospace", fontSize: "6px", color: this.hex(UGC_CYAN), align: "center" })
      .setOrigin(0.5, 0.5);
    this.reviewPanel = this.add.container(0, 0, [this.reviewBg, this.reviewText]).setDepth(DEPTH_HUD + 2).setVisible(false);
  }
}
